import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import qs from 'qs';
import interceptorMfd from './interceptor-mfd';
import interceptorPassport from './interceptor-passport';

interface RequestConfig {
  options: AxiosRequestConfig;
  requestInterceptor?: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig;
  responseInterceptor?: (response: AxiosResponse) => any;
  requestErrorHandler?(error: AxiosError): AxiosError | Promise<AxiosError>;
  responseErrorHandler?(error: AxiosError): AxiosError | Promise<AxiosError>;
}

enum RequestConfigKey {
  Default = 'default',
  Mfd = 'mfd',
  Accurate = 'accurate',
  Passport = 'passport',
}

const requestConfigs: Record<RequestConfigKey, RequestConfig> = {
  [RequestConfigKey.Default]: {
    options: {
      baseURL: '',
      timeout: 60000,
    },
  },
  [RequestConfigKey.Mfd]: {
    options: {
      baseURL: import.meta.env.VITE_MFD_BASE_URL,
      timeout: 120000,
    },
    ...interceptorMfd,
  },
  [RequestConfigKey.Accurate]: {
    options: {
      baseURL: import.meta.env.VITE_ACCURATE_BASE_URL,
      timeout: 60000,
    },
    ...interceptorMfd,
  },
  [RequestConfigKey.Passport]: {
    options: {
      baseURL: import.meta.env.VITE_PASSPORT_BASE_URL,
      timeout: 60000,
    },
    ...interceptorPassport,
  },
};

/**
 * fix: https://github.com/axios/axios/issues/1443
 */
function setupParamsSerializer(request: AxiosInstance) {
  request.defaults.paramsSerializer = {
    serialize: function (params) {
      return qs.stringify(params, { indices: false }); // param=value1&param=value2
    },
  };
}

function createAxiosInstance(key: RequestConfigKey) {
  const rc = requestConfigs[key];
  const inst = axios.create(rc.options);
  if (rc.requestInterceptor) {
    inst.interceptors.request.use(rc.requestInterceptor, rc.requestErrorHandler);
  }
  if (rc.responseInterceptor) {
    inst.interceptors.response.use(rc.responseInterceptor, rc.responseErrorHandler);
  }
  setupParamsSerializer(inst);
  return inst;
}

export default {
  dft: createAxiosInstance(RequestConfigKey.Default),
  mfd: createAxiosInstance(RequestConfigKey.Mfd),
  accurate: createAxiosInstance(RequestConfigKey.Accurate),
  passport: createAxiosInstance(RequestConfigKey.Passport),
};
